/**
 * ThankYouDesignEditor — 감사장 디자인(HTML/Liquid 템플릿) 코드 에디터
 *
 * TemplateEditor 패턴의 축소판: 좌측 Monaco(HTML) + 우측 샘플 데이터 라이브 프리뷰(iframe).
 * 스니펫 바로 Liquid placeholder를 커서 위치에 삽입하고, 에셋 패널에서 업로드된
 * 폰트/이미지 URL을 복사해 쓴다(다른 디자인의 에셋도 노출 — 감사장 간 재사용).
 * 저장은 PATCH /api/admin/thankyou-designs/:id — 응답 warnings는 토스트로 안내.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import Editor from '@monaco-editor/react'
import type { editor } from 'monaco-editor'
import { useApiFetch } from '../hooks/useApiFetch'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Select } from '../ui/Select'
import { Spinner } from '../ui/Spinner'
import { EmptyState } from '../ui/EmptyState'
import { renderThankYouPreview } from '../lib/thankyou-render'
import type { ThankYouDesign, ThankYouDesignAsset } from '../lib/thankyou-types'
import styles from './ThankYouDesignEditor.module.css'

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '활성' },
  { value: 'INACTIVE', label: '비활성' },
]

/** 도움말 팝오버에 안내할 사용 가능한 변수 (renderThankYouHtml 계약 기준) */
const HELP_VARS: { code: string; desc: string; legacy?: boolean }[] = [
  { code: '{{ message | nl2br }}', desc: '인사 메시지 (nl2br로 줄바꿈 유지)' },
  { code: '{{ name1 }} / {{ name2 }}', desc: '보내는 이 1·2 (한쪽만 채우면 그 하나만 표시)' },
  { code: '{{ names }}', desc: '보내는 이 합본 — 레거시, 신규는 name1/name2 사용', legacy: true },
  { code: '{{ photo1 }}, {{ photo2 }} …', desc: '넘버링 사진 슬롯 — 이걸 넣어야 카드 에디터에 업로드 칸이 생깁니다' },
  { code: '{{ photoUrl }}', desc: '레거시 단일 사진(=photo1 별칭) — 업로드 칸 안 생김', legacy: true },
  { code: "{{ weddingDate | formatDate: 'korean' }}", desc: '예식일 — 포맷: korean / day / time / 기본(YYYY.MM.DD)' },
]

/** 태그 도움말 팝오버 — 클릭 토글, 외부 클릭·ESC로 닫힘 */
function TagHelpPopover() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    function onClickOutside(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onClickOutside)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onClickOutside)
    }
  }, [open])

  return (
    <div ref={ref} className={styles.helpRoot}>
      <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        ? 도움말
      </Button>
      {open && (
        <div className={styles.helpPopover} role="dialog" aria-label="사용 가능한 태그">
          <div className={styles.helpTitle}>사용 가능한 변수</div>
          <table className={styles.helpTable}>
            <tbody>
              {HELP_VARS.map((v) => (
                <tr key={v.code} className={v.legacy ? styles.helpLegacy : undefined}>
                  <td><code className={styles.helpCode}>{v.code}</code></td>
                  <td className={styles.helpDesc}>{v.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.helpNote}>
            <p>
              사진 칸은 반드시 <code className={styles.helpCode}>photo1</code>,{' '}
              <code className={styles.helpCode}>photo2</code>처럼 번호를 붙이세요. 번호 개수만큼 카드 만드는
              사람에게 업로드 칸이 나타납니다.
            </p>
            <p>
              빈 슬롯 숨기기:{' '}
              <code className={styles.helpCode}>{'{% if photo1 %}<img src="{{ photo1 }}">{% endif %}'}</code>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

/** Liquid placeholder 삽입 스니펫 */
const SNIPPETS = [
  { label: '메시지', code: '{{ message | nl2br }}' },
  { label: '이름(합본)', code: '{{ names }}' },
  { label: '이름1', code: '{{ name1 }}' },
  { label: '이름2', code: '{{ name2 }}' },
  { label: '사진', code: '{% if photoUrl %}<img src="{{ photoUrl }}" alt="" />{% endif %}' },
  // weddingDate는 raw(YYYY-MM-DD) 주입 — formatDate 필터로 표시 포맷 선택 (korean: N년 N월 N일 / 기본: YYYY.MM.DD / day: N요일)
  { label: '예식일', code: "{{ weddingDate | formatDate: 'korean' }}" },
  { label: '예식일(점)', code: '{{ weddingDate | formatDate }}' },
]

export function ThankYouDesignEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { data: list, isLoading } = useApiFetch<ThankYouDesign[]>('/api/admin/thankyou-designs?includeInactive=true')

  const [design, setDesign] = useState<ThankYouDesign | null>(null)
  const [srcDoc, setSrcDoc] = useState('')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)

  // 목록 로드되면 id로 찾아 편집용 로컬 상태로 복사
  useEffect(() => {
    if (!list || !id) return
    const found = list.find((d) => d.id === id)
    if (found) setDesign(found)
  }, [list, id])

  // html 변경 → 프리뷰 재렌더 (300ms debounce). html 외 필드 입력 시 깜빡임 방지를 위해 html만 의존
  useEffect(() => {
    if (design?.html == null) return
    const html = design.html
    const t = setTimeout(() => {
      renderThankYouPreview(html).then(setSrcDoc).catch(() => setSrcDoc(''))
    }, 300)
    return () => clearTimeout(t)
  }, [design?.html])

  // 에셋 패널: 현재 디자인 + 다른 디자인의 에셋 (감사장 간 재사용)
  const assetGroups = useMemo(() => {
    const mine = design?.assets ?? []
    const others = (list ?? [])
      .filter((d) => d.id !== id)
      .flatMap((d) => (d.assets ?? []).map((a) => ({ ...a, from: d.name })))
    return { mine, others }
  }, [list, design, id])

  const insertSnippet = (code: string) => {
    const ed = editorRef.current
    if (!ed) return
    const sel = ed.getSelection()
    if (!sel) return
    ed.executeEdits('snippet', [{ range: sel, text: code, forceMoveMarkers: true }])
    ed.focus()
  }

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      addToast({ type: 'success', message: 'URL을 복사했습니다' })
    } catch {
      addToast({ type: 'info', message: '복사 실패 — URL을 직접 선택해 복사하세요' })
    }
  }

  // 이미지/폰트 업로드 → S3 presign PUT → assets에 추가 (저장 시 PATCH에 포함)
  const uploadAsset = async (file: File) => {
    setUploading(true)
    try {
      const presign = await apiFetch<{ key: string; uploadUrl: string; publicUrl: string }>(
        '/api/admin/thankyou-designs/presign',
        { method: 'POST', body: JSON.stringify({ fileName: file.name, contentType: file.type }) },
      )
      const put = await fetch(presign.uploadUrl, { method: 'PUT', body: file, headers: { 'content-type': file.type } })
      if (!put.ok) throw new Error('S3 업로드에 실패했습니다')
      const category: ThankYouDesignAsset['category'] = file.type.startsWith('image/') ? 'image' : 'font'
      setDesign((d) =>
        d ? { ...d, assets: [...(d.assets ?? []), { originalPath: file.name, s3Url: presign.publicUrl, category }] } : d,
      )
      await copyUrl(presign.publicUrl)
    } catch (err) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : '업로드에 실패했습니다' })
    } finally {
      setUploading(false)
    }
  }

  const save = async () => {
    if (!design) return
    setSaving(true)
    try {
      const res = await apiFetch<{ id: string; warnings: string[] }>(`/api/admin/thankyou-designs/${design.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: design.name,
          status: design.status,
          sortOrder: design.sortOrder,
          html: design.html,
          assets: design.assets ?? [],
          thumbnailUrl: design.thumbnailUrl ?? null,
          aspectRatio: design.aspectRatio,
        }),
      })
      addToast({ type: 'success', message: '저장되었습니다' })
      for (const w of res.warnings ?? []) addToast({ type: 'info', message: w })
    } catch (err) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : '저장에 실패했습니다' })
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) return <div className={styles.center}><Spinner /></div>
  if (!design) {
    return (
      <EmptyState
        title="디자인을 찾을 수 없습니다"
        action={<Button variant="outline" onClick={() => navigate('/wedding/thankyou-designs')}>목록으로</Button>}
      />
    )
  }

  return (
    <div className={styles.page}>
      {/* 뒤로가기 */}
      <div className={styles.navBar}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/wedding/thankyou-designs')}>
          ← 목록
        </Button>
      </div>

      {/* 메타 바 */}
      <div className={styles.metaBar}>
        <Input label="이름" value={design.name} onChange={(e) => setDesign({ ...design, name: e.target.value })} />
        <Select label="상태" value={design.status} options={STATUS_OPTIONS}
          onChange={(e) => setDesign({ ...design, status: e.target.value as ThankYouDesign['status'] })} />
        <Input label="정렬" type="number" value={String(design.sortOrder)}
          onChange={(e) => setDesign({ ...design, sortOrder: Number(e.target.value) })} />
        <Input label="비율 (HTML .frame과 일치)" value={design.aspectRatio}
          onChange={(e) => setDesign({ ...design, aspectRatio: e.target.value })} />
        <Button variant="primary" onClick={save} loading={saving}>저장</Button>
      </div>

      {/* 스니펫 + 업로드 바 */}
      <div className={styles.toolBar}>
        <span className={styles.toolLabel}>삽입:</span>
        {SNIPPETS.map((s) => (
          <Button key={s.label} size="sm" variant="secondary" onClick={() => insertSnippet(s.code)}>{s.label}</Button>
        ))}
        <label className={styles.uploadLabel}>
          이미지/폰트 업로드
          <input type="file" accept="image/*,.woff,.woff2,.ttf,.otf" hidden disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadAsset(f); e.target.value = '' }} />
        </label>
        {uploading && <Spinner />}
        <div className={styles.toolSpacer} />
        <TagHelpPopover />
      </div>

      <div className={styles.workArea}>
        {/* 좌: Monaco */}
        <div className={styles.editorPane}>
          {/* 감사장 HTML 도 Liquid({{ }}·{% %})라 'liquid' 모드로 본다 — basic-language 라
              진단이 없어 태그·필터가 허위 문법 오류로 잡히지 않는다.
              path 는 모델 URI 로, 디자인별 분리 + 다른 언어 모델과의 마커 혼입 방지용. */}
          <Editor
            height="100%"
            path={`thankyou-design/${id ?? 'new'}.liquid`}
            language="liquid"
            theme="vs-dark"
            value={design.html}
            onChange={(v) => setDesign((d) => (d ? { ...d, html: v ?? '' } : d))}
            onMount={(ed) => { editorRef.current = ed }}
            options={{ minimap: { enabled: false }, wordWrap: 'on', fontSize: 13 }}
          />
        </div>

        {/* 우: 프리뷰 + 에셋 패널 */}
        <div className={styles.sidePane}>
          <div className={styles.preview} style={{ aspectRatio: design.aspectRatio.replace('/', ' / ') }}>
            {srcDoc && <iframe className={styles.previewFrame} srcDoc={srcDoc} sandbox="" title="감사장 미리보기" />}
          </div>

          <div className={styles.assets}>
            <div className={styles.assetsTitle}>에셋 URL</div>
            {assetGroups.mine.length === 0 && assetGroups.others.length === 0 && (
              <p className={styles.assetsEmpty}>업로드된 에셋이 없습니다</p>
            )}
            {assetGroups.mine.map((a) => (
              <div key={`mine-${a.s3Url}`} className={styles.assetRow}>
                <span className={styles.assetBadge} data-category={a.category}>{a.category}</span>
                <span className={styles.assetName} title={a.s3Url}>{a.originalPath}</span>
                <Button size="sm" variant="secondary" onClick={() => void copyUrl(a.s3Url)}>복사</Button>
              </div>
            ))}
            {assetGroups.others.length > 0 && <div className={styles.assetsSub}>다른 디자인</div>}
            {assetGroups.others.map((a) => (
              <div key={`${a.from}-${a.s3Url}`} className={styles.assetRow}>
                <span className={styles.assetBadge} data-category={a.category}>{a.category}</span>
                <span className={styles.assetName} title={a.s3Url}>{a.from} / {a.originalPath}</span>
                <Button size="sm" variant="secondary" onClick={() => void copyUrl(a.s3Url)}>복사</Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
